<template>
  <v-card class="sw-date-picker" elevation="8" rounded="xl">
    <div class="sw-date-picker__body">
      <aside v-if="showPresetSidebar" class="sw-date-picker__sidebar">
        <p class="sw-date-picker__sidebar-title">{{ title }}</p>
        <button
          v-for="preset in presets"
          :key="preset.id"
          type="button"
          class="sw-date-picker__preset"
          :class="{ 'sw-date-picker__preset--active': activePreset === preset.id }"
          @click="selectPreset(preset.id)"
        >
          <v-icon :icon="preset.icon" size="18" class="sw-date-picker__preset-icon" />
          <span>{{ preset.label }}</span>
        </button>
      </aside>

      <div class="sw-date-picker__main">
        <div class="sw-date-picker__calendar-shell">
          <v-date-picker
            v-model="draftDate"
            color="primary"
            :min="min"
            :max="max"
            :type="type"
            :show-adjacent-months="true"
            hide-header
            elevation="0"
            class="sw-date-picker__calendar"
            @update:model-value="onCalendarChange"
          />
        </div>
      </div>
    </div>

    <div v-if="!immediate" class="sw-date-picker__footer">
      <v-btn
        variant="outlined"
        class="sw-date-picker__btn sw-date-picker__btn--cancel"
        @click="onCancel"
      >
        Cancel
      </v-btn>
      <v-btn
        variant="flat"
        class="sw-date-picker__btn sw-date-picker__btn--apply sw-primary-btn"
        @click="onApply"
      >
        Apply
      </v-btn>
    </div>
  </v-card>
</template>

<script>
const PRESET_ITEMS = [
  { id: "today", label: "Today", icon: "mdi-calendar-today" },
  { id: "yesterday", label: "Yesterday", icon: "mdi-history" },
  { id: "last7", label: "Last 7 Days", icon: "mdi-calendar-range" },
  { id: "last30", label: "Last 30 Days", icon: "mdi-calendar-month" },
  { id: "thisMonth", label: "This Month", icon: "mdi-calendar-blank-outline" },
];

function startOfDay(date) {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return startOfDay(value);
  const parsed = new Date(`${String(value).split("T")[0]}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : startOfDay(parsed);
}

function toModelValue(date) {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function presetDate(id) {
  const today = startOfDay(new Date());

  switch (id) {
    case "today":
      return today;
    case "yesterday": {
      const value = new Date(today);
      value.setDate(value.getDate() - 1);
      return value;
    }
    case "last7": {
      const value = new Date(today);
      value.setDate(value.getDate() - 6);
      return value;
    }
    case "last30": {
      const value = new Date(today);
      value.setDate(value.getDate() - 29);
      return value;
    }
    case "thisMonth":
      return new Date(today.getFullYear(), today.getMonth(), 1);
    default:
      return today;
  }
}

function detectPreset(date) {
  if (!date) return null;

  for (const preset of PRESET_ITEMS) {
    const candidate = presetDate(preset.id);
    if (toModelValue(candidate) === toModelValue(date)) {
      return preset.id;
    }
  }

  return null;
}

export default {
  name: "SwDatePicker",

  props: {
    modelValue: { type: [String, Date, Array], default: null },
    min: { type: [String, Date], default: undefined },
    max: { type: [String, Date], default: undefined },
    type: { type: String, default: "date" },
    title: { type: String, default: "Select Date" },
    showPresets: { type: Boolean, default: true },
    immediate: { type: Boolean, default: false },
  },

  emits: ["update:modelValue", "apply", "cancel"],

  computed: {
    showPresetSidebar() {
      return this.showPresets && this.type === "date";
    },
  },

  data() {
    return {
      draftDate: toDate(this.modelValue),
      activePreset: detectPreset(toDate(this.modelValue)),
      presets: PRESET_ITEMS,
    };
  },

  watch: {
    modelValue(value) {
      this.draftDate = toDate(value);
      this.activePreset = detectPreset(this.draftDate);
    },
  },

  methods: {
    selectPreset(id) {
      this.draftDate = presetDate(id);
      this.activePreset = id;
      if (this.immediate) this.onApply();
    },

    onCalendarChange(value) {
      this.draftDate = toDate(value);
      this.activePreset = detectPreset(this.draftDate);
      if (this.immediate) this.onApply();
    },

    onApply() {
      const value = this.type === "month"
        ? this.draftDate
          ? `${this.draftDate.getFullYear()}-${String(this.draftDate.getMonth() + 1).padStart(2, "0")}`
          : null
        : toModelValue(this.draftDate);

      this.$emit("update:modelValue", value);
      this.$emit("apply", value);
    },

    onCancel() {
      this.draftDate = toDate(this.modelValue);
      this.activePreset = detectPreset(this.draftDate);
      this.$emit("cancel");
    },
  },
};
</script>
